import { useMemo, useCallback } from 'react'

import type { DoctorForUser } from '@unni/api-client/contracts/doctor'
import type { ServiceOffer } from '@/features/service-offer/service-offer.type'
import type { ReviewsPaginationResponseInternal } from '@/features/review/api/review.type'
import type { HospitalView } from '@/features/hospital/types/hospital.type'
import type { User } from '@/features/member/member.type'

/**
 * Doctor data processing hook
 */
export const useDoctorData = (doctors: DoctorForUser[]) => {
  // All doctors are considered active for now
  const activeDoctors = useMemo(() => doctors, [doctors])

  // Group doctors by major (specialization)
  const doctorsBySpecialization = useMemo(() => {
    const groups: Record<string, DoctorForUser[]> = {}
    activeDoctors.forEach((doctor) => {
      if (doctor.majorName) {
        if (!groups[doctor.majorName]) {
          groups[doctor.majorName] = []
        }
        groups[doctor.majorName].push(doctor)
      }
    })
    return groups
  }, [activeDoctors])

  // Get featured doctors (e.g., those with highest ratings)
  const featuredDoctors = useMemo(
    () =>
      activeDoctors
        .filter((doctor) => doctor.statistics?.rating && doctor.statistics.rating >= 4.5)
        .sort((a, b) => (b.statistics?.rating || 0) - (a.statistics?.rating || 0))
        .slice(0, 3),
    [activeDoctors],
  )

  return {
    activeDoctors,
    doctorsBySpecialization,
    featuredDoctors,
    totalDoctorsCount: activeDoctors.length,
  }
}

/**
 * Event data processing hook
 */
export const useEventData = (events: ServiceOffer[]) => {
  // All events are considered active for now
  const activeEvents = useMemo(() => events, [events])

  // Sort events by rating (simplified)
  const sortedEvents = useMemo(
    () =>
      [...activeEvents].sort((a, b) => {
        // Sort by rating
        return (b.rating?.totalRating || 0) - (a.rating?.totalRating || 0)
      }),
    [activeEvents],
  )

  // Get featured events (first few events)
  const featuredEvents = useMemo(() => sortedEvents.slice(0, 3), [sortedEvents])

  return {
    activeEvents,
    sortedEvents,
    featuredEvents,
    totalEventsCount: activeEvents.length,
  }
}

/**
 * Review data processing hook
 */
export const useReviewData = (reviews?: ReviewsPaginationResponseInternal) => {
  const reviewData = useMemo(() => {
    if (!reviews) return null

    const { contents, totalElementNumber } = reviews

    // Calculate average rating from reviews
    const averageRating =
      contents.length > 0 ? contents.reduce((sum, review) => sum + (review.totalRating || 0), 0) / contents.length : 0

    // Calculate rating distribution
    const ratingDistribution = contents.reduce(
      (acc, review) => {
        const rating = Math.floor(review.totalRating || 0)
        acc[rating] = (acc[rating] || 0) + 1
        return acc
      },
      {} as Record<number, number>,
    )

    // Get recent reviews (first 5 reviews)
    const recentReviews = contents.slice(0, 5)

    return {
      items: contents,
      totalCount: totalElementNumber,
      averageRating,
      ratingDistribution,
      recentReviews,
      hasReviews: contents.length > 0,
    }
  }, [reviews])

  return reviewData
}

/**
 * Hospital badge processing hook
 */
export const useHospitalBadges = (hospital: HospitalView) => {
  const badges = useMemo(() => {
    const allBadges = hospital.badges || []

    // All badges are treated equally for now
    const certificationBadges = allBadges
    const specialtyBadges = []
    const achievementBadges = []

    return {
      all: allBadges,
      certifications: certificationBadges,
      specialties: specialtyBadges,
      achievements: achievementBadges,
      hasAnyBadges: allBadges.length > 0,
    }
  }, [hospital.badges])

  return badges
}

/**
 * Main hospital detail data hook
 * Combines all data processing hooks for the hospital detail page
 */
export const useHospitalDetail = (
  hospital: HospitalView,
  doctors: DoctorForUser[],
  events: ServiceOffer[],
  reviews?: ReviewsPaginationResponseInternal,
  user?: User | null,
) => {
  const doctorData = useDoctorData(doctors)
  const eventData = useEventData(events)
  const reviewData = useReviewData(reviews)
  const badges = useHospitalBadges(hospital)

  // Check if user has visited this hospital before
  const hasVisitedBefore = useMemo(() => {
    if (!user) return false
    // This would typically check against user's visit history
    // For now, return false as placeholder
    return false
  }, [user])

  // Check if user can book appointment
  const canBookAppointment = useMemo(() => {
    return !!(user && doctorData.activeDoctors.length > 0)
  }, [user, doctorData.activeDoctors.length])

  // Generate hospital summary stats
  const summaryStats = useMemo(() => {
    return {
      totalDoctors: doctorData.totalDoctorsCount,
      totalEvents: eventData.totalEventsCount,
      totalReviews: reviewData?.totalCount || 0,
      averageRating: reviewData?.averageRating || 0,
      totalBadges: badges.all.length,
      hasOnlineBooking: true, // Assume all hospitals allow online booking
    }
  }, [
    doctorData.totalDoctorsCount,
    eventData.totalEventsCount,
    reviewData?.totalCount,
    reviewData?.averageRating,
    badges.all.length,
  ])

  return {
    hospital,
    doctors: doctorData,
    events: eventData,
    reviews: reviewData,
    badges,
    user,
    hasVisitedBefore,
    canBookAppointment,
    summaryStats,
  }
}
